<script setup>
import { WdsBadge, WdsIllustration, WdsPopup, WdsSectionMessage, WdsTabList, WdsTabPanels } from '@wds/components';
import { stripHtmlToPlainText } from '@/utils/stripHtmlToPlainText.js';

const props = defineProps({
  propertyTypeObj: {
    type: Object,
    required: true,
  },
  activeTab: {
    type: String,
    required: false,
    default: '',
  },
  tabsData: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  applySheet: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const emit = defineEmits(['closePopup', 'sustainabilityPinClick']);

const { t } = useI18n();

// COMPOSABLES
const { getFacilityIcon } = useFacilities();

// STORES
const testingStore = useTestingStore();

// ## EXPERIMENTS/FEATURE TOGGLES
const businessDetailsExperimentEnabled = testingStore.hasEnabledFeature('web_addPDPBusinessDetails');
const businessDetailsExperimentVars = businessDetailsExperimentEnabled
  ? testingStore.getFeatureVariables('web_addPDPBusinessDetails')
  : null;

// ### VARIABLES INITIALIZATION
const hasAwards = props.tabsData?.hoscars?.awards?.length;
const hasSustainability = props.tabsData?.sustainability;

// ### STATE INITIALIZATION
const propertyDetailsActiveTab = ref(props.activeTab);

// COMPUTED
const shouldDisplayBusinessDetails = computed(() => {
  const allowedCountries = businessDetailsExperimentVars?.countries_enabled?.countries || [];
  return (
    businessDetailsExperimentEnabled
    && props.tabsData?.businessDetails
    && allowedCountries.includes(props.tabsData?.businessDetails?.propertyCountryId)
  );
});

const propertyDetailsTabs = computed(() => {
  return [
    {
      name: 'about',
      label: t('t_ABOUT'),
    },
    {
      name: 'facilities',
      label: t('t_FACILITIES'),
    },
    {
      name: 'houserules',
      label: t('t_HOUSERULES'),
    },
    {
      name: 'more',
      label: t('t_MORE'),
      disabled: !hasSustainability && !hasAwards && !shouldDisplayBusinessDetails.value,
    },
  ];
});

const getPropertyDetailsActiveTab = computed(() => {
  return propertyDetailsActiveTab.value || toValue(propertyDetailsTabs)[0].name;
});

const sustainabilityData = computed(() => {
  return props.tabsData?.sustainability;
});

const awardsData = computed(() => {
  return props.tabsData?.hoscars?.awards;
});

const safeThingsToNote = computed(() => stripHtmlToPlainText(props.tabsData?.houserules?.thingsToNote));

const sustainabilityUrl = computed(() => {
  return t('t_SUSTAINABILITYURL') || '';
});

function getAwardTitle(award) {
  const place = t(`t_HOSCARSPLACE${award.place}`);
  let {
    key,
    other,
  } = award;
  key = t(`t_${key}`);

  return `${place}, ${key} ${other}`.trim();
}
function closePropertyDetailsPopup() {
  emit('closePopup');
}
function handleSustainabilityPinClick(propertySustainabilityInfo) {
  emit('sustainabilityPinClick', propertySustainabilityInfo);
}
function handlePropertyDetailsTabClick(selectedTab) {
  propertyDetailsActiveTab.value = selectedTab?.tab || null;
}
</script>

<template>
  <WdsPopup
    is-open
    :apply-sheet="applySheet"
    :heading="$t('t_PROPERTYDETAILS')"
    close-button
    head-divider
    @popup-close="closePropertyDetailsPopup"
  >
    <template #popupContent>
      <div class="property-details-container">
        <WdsTabList
          :active-tab="getPropertyDetailsActiveTab"
          :tab-list="propertyDetailsTabs"
          class="property-details-tabs"
          @tab-clicked="handlePropertyDetailsTabClick"
        />
        <WdsTabPanels
          :active-panel="getPropertyDetailsActiveTab"
          class="property-details-panels"
        >
          <template #about>
            <div
              class="panel-about"
              v-html="tabsData?.about?.description"
            />
          </template>
          <template #facilities>
            <div class="panel-facilities">
              <div
                v-for="fGroup in tabsData?.facilities"
                :key="`fGroup${fGroup.id}`"
                class="facility-category"
              >
                <h3 class="facility-category-name">
                  {{ $t(`t_${fGroup.name}`) }}
                </h3>
                <ul class="facility-items">
                  <li
                    v-for="facilityItem in fGroup.facilities"
                    :key="`fItem${facilityItem.id}`"
                    class="facility-item"
                  >
                    <WdsBadge
                      :icon="getFacilityIcon(facilityItem.name)"
                      class="facility-badge"
                      theme="blue-light"
                    />
                    <span>{{ $t(`t_${facilityItem.name}`) }}</span>
                  </li>
                </ul>
              </div>
            </div>
          </template>
          <template #houserules>
            <div class="panel-houserules">
              <div class="policy-section">
                <h3 class="stay-policy-title">
                  {{ $t(propertyTypeObj.transcode_s) }} {{ $t('t_POLICIES') }}
                </h3>
                <PropertyHwHouseRulesStayPolicy
                  :additional-rules="tabsData?.houserules?.additionalRules"
                  :other-house-rules="tabsData?.houserules?.otherHouseRules"
                  :check-in-time="tabsData?.houserules?.checkInTime"
                  :check-out-time="tabsData?.houserules?.checkOutTime"
                />
              </div>
              <div
                v-if="tabsData?.houserules?.cancellationPolicy?.freeCancellationEnabled"
                class="policy-section"
              >
                <h3>{{ $t('t_CANCELPOLICY') }}</h3>
                <strong>{{ $t('t_DEPOSITONLYCANCELLATIONPOLICYTITLE') }}</strong>
                <p class="cancellation-policy-description">
                  {{
                    $t('t_DEPOSITONLYCANCELLATIONPOLICY', {
                      days: tabsData?.houserules?.cancellationPolicy?.canxDays,
                    })
                  }}
                </p>
              </div>
              <div
                v-if="safeThingsToNote"
                class="policy-section"
              >
                <h3>{{ $t('t_THINGSTONOTE') }}</h3>
                <p class="things-to-note-description">
                  {{ safeThingsToNote }}
                </p>
              </div>
            </div>
          </template>
          <template #more>
            <div class="panel-more">
              <div
                v-if="hasSustainability"
                class="more-section sustainability-section"
              >
                <h3>
                  {{ $t('t_SUSTAINABILITY') }}
                </h3>
                <div class="sustainability-card">
                  <WdsBadge
                    :icon="sustainabilityData.icon"
                    class="icon sustainability"
                  />

                  <div class="sustainability-text-wrapper">
                    <h5 class="body-1-bld">
                      {{ $t('t_SUSTAINABILITYCERTIFIED') }}
                    </h5>
                    <p
                      v-if="sustainabilityData.description"
                      class="body-2-reg"
                    >
                      {{ sustainabilityData.description }}
                    </p>
                    <a
                      :href="sustainabilityUrl"
                      class="link-2-reg"
                      target="_blank"
                      @click="handleSustainabilityPinClick(sustainabilityData)"
                    >{{ $t('t_LEARNMORE') }}</a>
                  </div>
                </div>
              </div>
              <div
                v-if="hasAwards"
                class="more-section awards-section"
              >
                <h3>
                  {{ $t('t_AWARDS') }}
                </h3>
                <div class="awards-list">
                  <WdsSectionMessage
                    v-for="(award, index) in awardsData"
                    :key="`${award.key}-${index}`"
                    :sub-title="award.year.toString()"
                    :title="getAwardTitle(award)"
                    class="award"
                    icon="trophy"
                  />
                </div>
              </div>
              <div
                v-if="shouldDisplayBusinessDetails"
                class="more-section business-details-section"
              >
                <h3>
                  {{ $t('t_BUSINESSDETAILS') }}
                </h3>
                <div class="business-details-card">
                  <WdsIllustration
                    class="business-details-icon"
                    asset="document"
                    folder="sm"
                  />
                  <div class="business-details-text-wrapper">
                    <p class="business-details-description">
                      {{ $t('t_BUSINESSDETAILSDESCRIPTION') }}
                    </p>
                    <hr />
                    <p class="business-details-property-name">
                      {{ tabsData.businessDetails?.propertyName }}
                    </p>
                    <p>
                      {{ tabsData.businessDetails?.propertyAddress }}<br />
                      {{
                        [tabsData.businessDetails?.propertyCity, tabsData.businessDetails?.propertyCountry]
                          .filter(Boolean)
                          .join(', ')
                      }}
                    </p>
                    <p
                      v-if="tabsData.businessDetails?.propertyRegisterNumber"
                      class="business-details-property-register-number"
                    >
                      {{ $t('t_TRADEREGISTERNUMBER', { NUMBER: tabsData.businessDetails?.propertyRegisterNumber }) }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </WdsTabPanels>
      </div>
    </template>
  </WdsPopup>
</template>

<style lang="scss" scoped>
.property-details-container {
  height: 100%;
  max-width: wds-rem(912px);
  display: flex;
  flex-direction: column;

  .property-details-tabs {
    display: flex;
    justify-content: center;
    border-bottom: 1px solid $wds-color-ink-lightest;
  }

  .property-details-panels {
    flex-grow: 1;
    overflow-y: auto;
    padding: $wds-spacing-l;

    .panel-about {
      @include body-2-reg;

      padding: $wds-spacing-l;
    }

    .panel-facilities {
      display: flex;
      flex-direction: column;
      gap: $wds-spacing-m;
      padding: $wds-spacing-l;

      .facility-category {
        display: flex;
        flex-direction: column;
        gap: $wds-spacing-m;
        padding-bottom: $wds-spacing-m;
        border-bottom: 1px solid $wds-color-ink-lighter;

        .facility-category-name {
          @include title-5-bld;
        }

        .facility-items {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: $wds-spacing-s $wds-spacing-m;
          padding-left: $wds-spacing-s;

          .facility-item {
            display: inline-flex;
            gap: $wds-spacing-s;
            align-items: center;

            @include body-2-reg;

            .facility-badge {
              padding: wds-rem(12px);
              border-radius: 50%;
            }
          }
        }

        &:last-child {
          padding-bottom: 0;
          border-bottom: none;
        }
      }

      @include desktop {
        .facility-category .facility-items {
          grid-template-columns: 1fr 1fr 1fr 1fr;
        }
      }
    }

    .panel-houserules {
      display: flex;
      flex-direction: column;
      gap: $wds-spacing-l;
      padding: $wds-spacing-l;

      .policy-section {
        padding-bottom: $wds-spacing-l;
        border-bottom: 1px solid $wds-color-ink-lighter;

        &:last-child {
          padding-bottom: 0;
          border-bottom: none;
        }

        > h3 {
          @include title-5-bld;

          margin-bottom: $wds-spacing-m;
        }

        .cancellation-policy-description,
        .things-to-note-description {
          @include body-2-reg;

          white-space: pre-line;
        }
      }
    }

    .panel-more {
      padding: $wds-spacing-l;

      .more-section {
        margin-bottom: $wds-spacing-l;

        &:last-child {
          margin-bottom: 0;
        }

        > h3 {
          @include title-5-bld;

          margin-bottom: $wds-spacing-m;
        }
      }

      .sustainability-section {
        .sustainability-card {
          display: flex;
          align-items: flex-start;
          gap: $wds-spacing-m;
          padding: $wds-spacing-m;
          border-radius: $wds-border-radius-xl;
          border: wds-rem(1px) solid $wds-color-ink-lighter;

          :deep(.icon-container svg) {
            fill: $wds-color-green;
            width: wds-rem(40px);
            height: wds-rem(40px);
          }

          :deep(.badge-wrapper) {
            background-color: transparent;
            min-width: wds-rem(40px);
          }

          a {
            text-decoration: underline !important;
          }
        }
      }

      .awards-section {
        .awards-list {
          .award {
            margin-bottom: $wds-spacing-m;
            overflow-wrap: anywhere;
          }

          @include tablet-large {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            gap: $wds-spacing-m;

            .award {
              margin-bottom: 0;
            }
          }

          .award.message-wrapper {
            align-items: flex-start;
            z-index: 1;

            &:deep(.icon) {
              align-items: center;
              display: flex;
              justify-content: center;
              margin-left: $wds-spacing-xs;
              margin-right: $wds-spacing-l;
              min-width: $wds-spacing-l;
              position: relative;
              width: $wds-spacing-l;
              z-index: 1;

              &::before {
                background-color: $wds-color-sunset-lighter;
                border-radius: $wds-border-radius-m;
                content: '';
                height: $wds-spacing-xxl;
                left: 50%;
                position: absolute;
                transform: translateX(-50%);
                width: $wds-spacing-xxl;
                z-index: -1;
              }
            }
          }
        }
      }

      .business-details-section {
        .business-details-card {
          display: flex;
          align-items: flex-start;
          gap: $wds-spacing-m;
          padding: $wds-spacing-m;
          border-radius: $wds-border-radius-xl;
          border: wds-rem(1px) solid $wds-color-ink-lighter;

          .business-details-icon {
            width: wds-rem(32px);
            height: wds-rem(32px);
          }

          .business-details-text-wrapper {
            @include body-1-reg;

            .business-details-description {
              @include body-2-reg;
            }

            hr {
              margin: $wds-spacing-m 0;
              height: 1px;
              border: none;
              border-top: 1px solid $wds-color-ink-lighter;
            }

            .business-details-property-name {
              @include body-1-bld;
            }

            .business-details-property-register-number {
              margin-top: $wds-spacing-m;
            }
          }
        }
      }
    }
  }

  @include desktop {
    height: calc(100vh - 8rem);
    max-height: wds-rem(688px);
    width: calc(100vw - 4rem);
  }
}
</style>
